<div
  class="ct-card parsings-card"
  role="group"
  [attr.aria-label]="
    'jobboard.platform.profile.sections.documents.parsings.aria'
      | transloco: { remaining: parsingsRemaining(), total: parsingsPerMonth }
  "
>
  <div class="parsings-meter">
    <div class="parsings-meter-header">
      <p class="parsings-meter-label">
        {{
          'jobboard.platform.profile.sections.documents.parsings.available'
            | transloco: { remaining: parsingsRemaining(), total: parsingsPerMonth }
        }}
      </p>
    </div>

    <p-meterGroup
      [value]="parsingsMeterValue()"
      [min]="0"
      [max]="parsingsPerMonth"
      orientation="horizontal"
      labelPosition="end"
      labelOrientation="horizontal"
    >
      <ng-template #label let-meters>
        <ul class="parsings-legend">
          @for (item of meters; track item.label) {
            <li>
              <span
                class="parsings-legend-marker"
                [style.backgroundColor]="item.color"
                aria-hidden="true"
              ></span>
              <span class="parsings-legend-label">{{ item.label }}</span>
              <b class="parsings-legend-value">{{ item.value }}</b>
            </li>
          }
        </ul>
      </ng-template>
    </p-meterGroup>

    <p class="parsings-meter-hint">
      @if (parsingsRemaining() > 0) {
        {{ 'jobboard.platform.profile.sections.documents.parsings.hint' | transloco }}
      } @else {
        {{ 'jobboard.platform.profile.sections.documents.parsings.hint-exhausted' | transloco }}
      }
    </p>
  </div>
</div>

@if (!hasMainCv()) {
  <ct-lib-info-banner type="warning">
    <div class="missing-main-banner">
      <p>{{ 'jobboard.platform.profile.sections.documents.missing-main-banner' | transloco }}</p>
      <app-button
        htmlType="button"
        [iconStart]="icons.upload"
        (click)="openStandardMainUploadModal()"
      >
        {{ 'jobboard.platform.profile.sections.documents.upload.standard' | transloco }}
      </app-button>
    </div>
  </ct-lib-info-banner>
}

<div class="table-wrapper">
  <div class="actions">
    <ct-lib-table-search-input
      [placeholder]="'jobboard.platform.profile.sections.documents.search-placeholder'"
      (search)="onSearch($event)"
    />
    <ct-lib-dropdown [dropdown]="uploadMenu" [autowidth]="true" [closeOnClick]="true" #uploadDropdown>
      <app-button [iconStart]="icons.plus" class="icon-end">
        {{ 'jobboard.platform.profile.sections.documents.upload.button' | transloco }}
        <ct-lib-rotating-chevron
          class="ms-1"
          [baseState]="ChevronState.DOWN"
          [toggledState]="ChevronState.UP"
          [toggle]="uploadDropdown.openState()"
        />
      </app-button>
    </ct-lib-dropdown>
    <ng-template #uploadMenu>
      <ul>
        <li (click)="openUploadModal(uploadType.CV)">
          <fa-icon [icon]="icons.fileUser" size="lg" aria-hidden="true"></fa-icon>
          <p>{{ 'jobboard.platform.profile.sections.documents.upload.cv' | transloco }}</p>
        </li>
        <li (click)="openUploadModal(uploadType.COVER_LETTER)">
          <fa-icon [icon]="icons.fileLines" size="lg" aria-hidden="true"></fa-icon>
          <p>{{ 'jobboard.platform.profile.sections.documents.upload.cover-letter' | transloco }}</p>
        </li>
        <li (click)="openUploadModal(uploadType.CERTIFICATE)">
          <fa-icon [icon]="icons.fileCertificate" size="lg" aria-hidden="true"></fa-icon>
          <p>{{ 'jobboard.platform.profile.sections.documents.upload.certificate' | transloco }}</p>
        </li>
        <li (click)="openUploadModal(uploadType.OTHER)">
          <fa-icon [icon]="icons.file" size="lg" aria-hidden="true"></fa-icon>
          <p>{{ 'jobboard.platform.profile.sections.documents.upload.other' | transloco }}</p>
        </li>
      </ul>
    </ng-template>
  </div>

  @if (tableRows().length === 0) {
    <ct-lib-empty-placeholder
      [icon]="icons.file"
      [title]="'jobboard.platform.profile.sections.documents.empty' | transloco"
      text=""
    />
  } @else if (visibleTableRows().length === 0) {
    <ct-lib-empty-placeholder
      [icon]="icons.file"
      [title]="'jobboard.platform.profile.sections.documents.search-empty' | transloco"
      text=""
    />
  } @else {
    <p-table
      [value]="visibleTableRows()"
      dataKey="id"
      [rowTrackBy]="trackByRow"
      [scrollable]="true"
      scrollHeight="40rem"
    >
      <ng-template #header>
        <tr>
          <th class="name-col">{{ 'jobboard.platform.profile.sections.documents.table.name' | transloco }}</th>
          <th>{{ 'jobboard.platform.profile.sections.documents.table.type' | transloco }}</th>
          <th>{{ 'jobboard.platform.profile.sections.documents.table.usage' | transloco }}</th>
          <th class="actions">{{ 'jobboard.platform.profile.sections.documents.table.actions' | transloco }}</th>
        </tr>
      </ng-template>
      <ng-template #body let-row>
        <tr>
          <td>
            @if (row.source === rowSource.DOCUMENT) {
              @if (row.document; as document) {
                <button type="button" class="name-link" (click)="previewDocument(document)">
                  <fa-icon [icon]="icons.paperclip" aria-hidden="true"></fa-icon>
                  <span>{{ row.name }}</span>
                </button>
              }
            } @else if (row.pending; as pending) {
              <button
                type="button"
                class="name-link"
                (click)="previewDocument({ id: pending.id, name: pending.name })"
              >
                <fa-icon [icon]="icons.paperclip" aria-hidden="true"></fa-icon>
                <span>{{ row.name }}</span>
              </button>
            }
          </td>
          <td>
            @if (row.source === rowSource.PENDING) {
              @if (row.pending; as pending) {
                <ct-lib-dropdown
                  [dropdown]="importActionMenu"
                  [autowidth]="true"
                  [closeOnClick]="true"
                  alignment="left"
                  #importActionDropdown
                >
                  <ct-lib-tag>
                    @if (pending.action === importedCvAction.UNSET) {
                      {{ 'jobboard.platform.profile.sections.documents.pending-badge' | transloco }}
                    } @else {
                      {{ importedCvActionKey(pending.action) | transloco }}
                    }
                    <ct-lib-rotating-chevron
                      class="ms-1"
                      [baseState]="ChevronState.DOWN"
                      [toggledState]="ChevronState.UP"
                      [toggle]="importActionDropdown.openState()"
                      size="lg"
                    />
                  </ct-lib-tag>
                </ct-lib-dropdown>
                <ng-template #importActionMenu>
                  <ul class="import-action-menu">
                    @for (option of typeOptions; track option.kind) {
                      <li
                        [class.active]="isImportedActionSelected(pending.action, option)"
                        (click)="setImportedCvAction(pending.id, typeOptionToImportedAction(option))"
                      >
                        <fa-icon [icon]="option.icon" size="lg" aria-hidden="true"></fa-icon>
                        <p>{{ option.labelKey | transloco }}</p>
                      </li>
                    }
                  </ul>
                </ng-template>
              }
            } @else if (row.document; as document) {
              <ct-lib-dropdown
                [dropdown]="typeMenu"
                [autowidth]="true"
                [closeOnClick]="true"
                alignment="left"
                #typeDropdown
              >
                <ct-lib-tag [interactable]="true">
                  {{ typeBadgeKey(document) | transloco }}
                  <ct-lib-rotating-chevron
                    class="ms-1"
                    [baseState]="ChevronState.DOWN"
                    [toggledState]="ChevronState.UP"
                    [toggle]="typeDropdown.openState()"
                    size="lg"
                  />
                </ct-lib-tag>
              </ct-lib-dropdown>
              <ng-template #typeMenu>
                <ul class="import-action-menu">
                  @for (option of typeOptions; track option.kind) {
                    <li
                      [class.active]="isTypeSelected(document, option)"
                      (click)="selectDocumentType(document.id, option)"
                    >
                      <fa-icon [icon]="option.icon" size="lg" aria-hidden="true"></fa-icon>
                      <p>{{ option.labelKey | transloco }}</p>
                    </li>
                  }
                </ul>
              </ng-template>
            }
          </td>
          <td>
            @if (row.document; as document) {
              @if (document.usedInApplications.length > 0) {
                <button
                  type="button"
                  class="usage-tag-button"
                  (click)="openUsageModal(document)"
                >
                  <ct-lib-tag [variant]="TagVariant.GRAY" [interactable]="true">
                    {{
                      'jobboard.platform.profile.sections.documents.usage-count'
                        | transloco: { count: document.usedInApplications.length }
                    }}
                  </ct-lib-tag>
                </button>
              } @else {
                <span class="text-muted">—</span>
              }
            }
          </td>
          <td class="actions">
            @if (row.source === rowSource.DOCUMENT) {
              @if (row.document; as document) {
                <fa-icon
                  [icon]="icons.eye"
                  size="lg"
                  [tooltip]="'jobboard.platform.profile.sections.documents.preview' | transloco"
                  [attr.aria-label]="'jobboard.platform.profile.sections.documents.preview' | transloco"
                  (click)="previewDocument(document)"
                ></fa-icon>
                <fa-icon
                  [icon]="icons.trash"
                  size="lg"
                  class="red"
                  [tooltip]="'jobboard.platform.profile.sections.documents.remove' | transloco"
                  [attr.aria-label]="'jobboard.platform.profile.sections.documents.remove' | transloco"
                  (click)="removeDocument(document.id)"
                ></fa-icon>
              }
            } @else if (row.pending; as pending) {
              <fa-icon
                [icon]="icons.eye"
                size="lg"
                [tooltip]="'jobboard.platform.profile.sections.documents.preview' | transloco"
                [attr.aria-label]="'jobboard.platform.profile.sections.documents.preview' | transloco"
                (click)="previewDocument({ id: pending.id, name: pending.name })"
              ></fa-icon>
              <fa-icon
                [icon]="icons.trash"
                size="lg"
                class="red"
                [tooltip]="'jobboard.platform.profile.sections.documents.import-actions.discard' | transloco"
                [attr.aria-label]="'jobboard.platform.profile.sections.documents.import-actions.discard' | transloco"
                (click)="discardPending(pending.id)"
              ></fa-icon>
            }
          </td>
        </tr>
      </ng-template>
    </p-table>
  }
</div>

<ct-lib-modal
  #uploadModal
  [small]="true"
  [label]="uploadModalTitleKey() | transloco"
  [footer]="uploadFooter"
  (onClose)="onUploadModalClose()"
>
  <ct-lib-file-input
    #uploadInput
    [control]="uploadControl"
    [multiple]="false"
    [accept]="fileTypes"
    [showAcceptLabel]="false"
    [label]="'jobboard.platform.profile.sections.documents.upload.file-label' | transloco"
    (filesChange)="onUploadFileChange()"
  ></ct-lib-file-input>

  @if (showAsMainCheckbox()) {
    <div class="form-check mt-3">
      <input
        class="form-check-input"
        type="checkbox"
        id="document-as-main"
        [ngModel]="asMain()"
        [ngModelOptions]="{ standalone: true }"
        (ngModelChange)="asMain.set($event)"
      />
      <label class="form-check-label" for="document-as-main">
        {{ 'jobboard.platform.profile.sections.documents.upload.as-main' | transloco }}
      </label>
    </div>
    @if (asMain()) {
      <p class="as-main-hint text-muted">
        {{ 'jobboard.platform.profile.sections.documents.upload.as-main-hint' | transloco }}
      </p>
    }
  }

  @if (showAutoMainHint()) {
    <p class="as-main-hint text-muted mt-3">
      {{ 'jobboard.platform.profile.sections.documents.upload.auto-main-hint' | transloco }}
    </p>
  }
</ct-lib-modal>

<ng-template #uploadFooter>
  <button type="button" class="ct-btn ct-btn-white-grey" (click)="uploadModal.close()">
    {{ 'jobboard.platform.profile.sections.documents.upload.cancel' | transloco }}
  </button>
  <button
    type="button"
    class="ct-btn ct-btn-primary"
    [disabled]="!selectedFile() || uploading()"
    (click)="submitUpload()"
  >
    {{ 'jobboard.platform.profile.sections.documents.upload.submit' | transloco }}
  </button>
</ng-template>

<ct-lib-modal
  #usageModal
  [small]="true"
  [label]="'jobboard.platform.profile.sections.documents.usage-modal.title' | transloco"
  [footer]="usageFooter"
  (onClose)="onUsageModalClose()"
>
  @if (usageApplications().length === 0) {
    <p class="text-muted">{{
      'jobboard.platform.profile.sections.documents.usage-modal.empty' | transloco
    }}</p>
  } @else {
    <ul class="usage-list">
      @for (application of usageApplications(); track application.id) {
        <li>
          <p class="usage-job">{{ application.jobTitle }}</p>
          <p class="usage-company text-muted">{{ application.companyName }}</p>
        </li>
      }
    </ul>
  }
</ct-lib-modal>

<ng-template #usageFooter>
  <button type="button" class="ct-btn ct-btn-primary" (click)="usageModal.close()">
    {{ 'jobboard.platform.profile.sections.documents.usage-modal.close' | transloco }}
  </button>
</ng-template>

<app-platform-document-viewer-modal #viewerModal />
